import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";

const principles = [
  {
    n: "01",
    title: "Artists own everything.",
    body: "We don't take your masters, your publishing or a piece of your name. Ever.",
  },
  {
    n: "02",
    title: "Transparency by default.",
    body: "Every royalty source, every dollar, every statement — exposed in your dashboard in real time.",
  },
  {
    n: "03",
    title: "Operational excellence.",
    body: "Music business is paperwork. We do the paperwork. You do the music.",
  },
  {
    n: "04",
    title: "Direct access.",
    body: "Talk to a real consultant — not a ticket queue, not an AI auto-responder.",
  },
];

export default function About() {
  return (
    <div className="bg-[#050505] text-white">
      <Seo
        title="About — Built by industry operators, for the artist"
        description="E-Quantum Consults was founded by industry operators who watched too many artists lose money, masters and leverage. Our principles: artists own everything, transparency by default, operational excellence, direct access."
        path="/about"
      />
      <section className="border-b border-white/10 py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="tag mb-6">// About</div>
          <h1 className="font-heading font-black text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tighter max-w-5xl">
            We exist for one
            <br />
            reason — <span className="text-[var(--primary)]">the artist.</span>
          </h1>
          <p className="mt-10 max-w-3xl text-zinc-300 text-lg leading-relaxed">
            E-Quantum Consults was founded by industry operators who watched
            too many talented artists hand over their masters, publishing and
            future earnings to companies that did the bare minimum. We built
            the firm we wished existed when we started — one that treats the
            artist like the principal, not the asset.
          </p>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="tag mb-4">// Principles</div>
            <h2 className="font-heading font-black text-3xl md:text-5xl tracking-tighter mb-6">
              How we operate.
            </h2>
            <p className="text-zinc-400 leading-relaxed">
              Four non-negotiables that govern every engagement we take on.
            </p>
          </div>
          <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10">
            {principles.map((p) => (
              <div key={p.n} className="bg-[#0a0a0a] p-8 card-hover">
                <div className="font-mono text-[10px] tracking-widest text-[var(--primary)] mb-6">
                  / {p.n}
                </div>
                <h3 className="font-heading font-bold text-xl mb-2">
                  {p.title}
                </h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 border-t border-white/10 bg-[#070707]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <div className="tag mb-4">// Coverage</div>
            <h2 className="font-heading font-black text-3xl md:text-5xl tracking-tighter mb-6">
              Every collection society.
              <br />
              <span className="text-[var(--primary)]">Every platform.</span>
            </h2>
            <p className="text-zinc-400 leading-relaxed max-w-2xl">
              We maintain working relationships and registered access to every
              income source your music touches — domestic and international.
              Nothing gets left on the table.
            </p>
          </div>
          <div className="md:col-span-5">
            <div className="border border-white/10 bg-[#0a0a0a] p-8">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--primary)] mb-4">
                // coverage
              </div>
              <ul className="space-y-3 text-sm text-zinc-300">
                <li>Performance royalties · domestic + international</li>
                <li>Mechanical royalties · domestic + international</li>
                <li>Neighbouring rights recovery</li>
                <li>Sync licensing opportunities</li>
                <li>Streaming platform placement</li>
                <li>Digital & platform-level content revenue</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 border-t border-white/10 text-center">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <h2 className="font-heading font-black text-4xl md:text-6xl tracking-tighter mb-6">
            Ready to take it back?
          </h2>
          <Link to="/audit" className="btn-primary inline-flex">
            Take the Artist Infrastructure Audit&trade; <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
